import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { Col, Grid, Radio, Row, Select, Space } from 'antd'
import { usePaymentOptions } from 'app/components/Core/Payment/Hooks/usePaymentOptions'
import QuoteCard from 'app/components/Core/Quote/Card'
import QuoteError from 'app/components/Core/Quote/Error'
import VoucherForm from 'app/components/Core/Voucher/Form'
import Container from 'app/components/UI-Elements/Layout/Container'
import KCol from 'app/components/UI-Elements/Layout/KCol'
import KFooter from 'app/components/UI-Elements/Layout/KFooter'
import { PaymentTypeEnum } from 'app/constants/Enums'
import { RecordType } from 'app/constants/GlobalTypes'
import { useFeedbacker } from 'app/lib/contexts/Feedbacker'
import { useShow } from 'app/lib/hooks/useApi'
import { useCurrentCustomer } from 'app/lib/hooks/useCurrent'
import useTitle from 'app/lib/hooks/useTitle'
import useTranslation from 'app/lib/hooks/useTranslation'
import EnvironmentService from 'app/lib/services/EnvironmentService'
import { getQueryString } from 'app/lib/utils/search-params'
import { PageProps } from 'app/pages/typing'
import React, { Fragment, useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import CreditCard from '../Containers/CreditCard'
import Invoice from '../Containers/Invoice'
import Paypal from '../Containers/Paypal'
import { PaymentContainerProps } from '../Containers/typing'

const portalId = 'payment-submit'

const stripePromise = loadStripe(EnvironmentService.getEnvValue('STRIPE_PUBLISHABLE_KEY'), {
  locale: 'de'
})

const Show: React.FC<PageProps> = (props) => {
  const { titlelizer } = useTitle(props)
  const { response: quote, execute, loading } = useShow<IQuote>(RecordType.quote)
  const { payment_id: rental_id } = useParams<{ payment_id: string }>()
  const { tRoute } = useTranslation({ scope: props.route.key })
  const { search } = useLocation()
  const { md } = Grid.useBreakpoint()
  const { toastify } = useFeedbacker()
  const { solvent } = useCurrentCustomer() || {}
  const { options } = usePaymentOptions()
  const [option, setOption] = useState<PaymentTypeEnum>(PaymentTypeEnum.credit_card)

  useEffect(() => {
    execute({ params: { includes: ['rental'], rental_id } })
    notifyOnCancel()
    titlelizer()
  }, [])

  useEffect(() => {
    if (solvent) {
      setOption(PaymentTypeEnum.invoice)
    }
  }, [solvent])

  const onChange = (value: PaymentTypeEnum) => {
    setOption(value)
  }

  const onVoucherSuccess = () => {
    execute({ params: { includes: ['rental'], rental_id } })
  }

  const notifyOnCancel = () => {
    const paymentOption = getQueryString(search, 'payment_option') as PaymentTypeEnum

    if (options.includes(paymentOption)) {
      toastify({
        content: tRoute(
          {
            id: 'cancelation_info'
          },
          {
            paymentOption: tRoute({ id: paymentOption })
          }
        ),
        type: 'info'
      })
    }
  }

  const currentOption = React.useMemo(() => {
    const componentProps: PaymentContainerProps = { quote, portalId, route: props.route }

    switch (option) {
      case 'credit_card':
        return <CreditCard {...componentProps} />
      case 'invoice':
        return <Invoice {...componentProps} />
      case 'paypal':
        return <Paypal {...componentProps} />
    }
  }, [option, quote])

  return (
    <QuoteError quote={quote}>
      <Container card={false} loading={loading}>
        <Elements stripe={stripePromise}>
          <Row gutter={[md ? 12 : 0, 24]} className={!md && 'flex-col-reverse'}>
            <If condition={md}>
              <KCol loading={loading} card xs={24} md={16}>
                <Row>
                  <Col xs={24} md={8}>
                    <Radio.Group value={option} onChange={(e) => onChange(e.target.value)}>
                      <Space direction="vertical">
                        {options.map((option) => (
                          <Radio value={option} key={option}>
                            {tRoute({ id: option })}
                          </Radio>
                        ))}
                      </Space>
                    </Radio.Group>
                  </Col>
                  <Col xs={24} md={16}>
                    <Space direction="vertical" className="full-width">
                      <h3>{tRoute({ id: 'payment_details' })}</h3>
                      <Fragment>{currentOption}</Fragment>
                      <KFooter sticky={false} id={portalId} />
                    </Space>
                  </Col>
                </Row>
              </KCol>
            </If>
            <If condition={!md}>
              <KCol
                loading={loading}
                span={24}
                card
                cardProps={{ title: tRoute({ id: 'payment_details' }) }}>
                <Row gutter={[0, 24]}>
                  <Col span={12}>
                    <Select
                      options={options.map((option) => ({
                        label: tRoute({ id: option }),
                        value: option
                      }))}
                      value={option}
                      onChange={onChange}
                    />
                  </Col>
                  <Col span={24}>{currentOption}</Col>
                </Row>
              </KCol>
            </If>
            <KCol card={loading} loading={loading} xs={24} md={8}>
              <QuoteCard quote={quote} variant="borderless">
                <VoucherForm
                  props={{ onSuccess: onVoucherSuccess }}
                  member={{
                    rental_id: quote?.rental?.id,
                    rental: quote?.rental,
                    code: quote?.rental?.voucher_code,
                    id: quote?.rental?.voucher_id
                  }}
                />
              </QuoteCard>
            </KCol>
          </Row>
        </Elements>
      </Container>
      <If condition={!md}>
        <KFooter id={portalId} />
      </If>
    </QuoteError>
  )
}

export default Show
